import { Search } from 'lucide-react';
import { useCallback, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useSearchField } from '@/components/list/use-search-field';
import { useRoleText } from '../roles/role-text';
import { useAssignableRoles } from '../roles/use-assignable-roles';
import type { PeopleFilters } from './use-people';

export const STATUSES = ['active', 'deactivated', 'invited'] as const;
export const ALL = 'all';

function SearchBox({
  typed,
  onType,
}: {
  readonly typed: string;
  readonly onType: (value: string) => void;
}) {
  const { t } = useTranslation('administration');
  const searchId = useId();
  return (
    <div className="list-toolbar-search">
      <label htmlFor={searchId} className="sr-only">
        {t('people.searchLabel')}
      </label>
      <Search
        aria-hidden="true"
        className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
      />
      <Input
        id={searchId}
        type="search"
        value={typed}
        onChange={(event) => onType(event.target.value)}
        placeholder={t('people.searchPlaceholder')}
        className="pl-9"
      />
    </div>
  );
}

export function PeopleToolbar({
  filters,
  setFilter,
  filtered,
  onClear,
  placeKind,
  placeId,
}: {
  readonly filters: PeopleFilters;
  readonly setFilter: (key: 'q' | 'role' | 'status' | 'sort', value: string | null) => void;
  readonly filtered: boolean;
  readonly onClear: () => void;
  readonly placeKind: 'tenants' | 'organizations';
  readonly placeId: string;
}) {
  const { t } = useTranslation('administration');
  const { name } = useRoleText();
  const { state: roles } = useAssignableRoles(placeKind, placeId);
  // The server searches: the address is written after a pause in typing, and the field follows
  // the address when «Azzera i filtri», or going back, changes it.
  const writeQuery = useCallback((q: string) => setFilter('q', q), [setFilter]);
  const [typed, setTyped] = useSearchField(filters.q, writeQuery);

  return (
    <div role="toolbar" aria-label={t('people.title')} className="list-toolbar">
      <SearchBox typed={typed} onType={setTyped} />
      <Select value={filters.roleId ?? ALL} onValueChange={(value) => setFilter('role', value)}>
        <SelectTrigger aria-label={t('people.roleFilter')} className="list-toolbar-chip">
          <span className="text-muted-foreground">{t('people.roleFilter')}:</span>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{t('people.all')}</SelectItem>
          {roles.roles.map((role) => (
            <SelectItem key={role.id} value={role.id}>
              {name(role)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={filters.status ?? ALL} onValueChange={(value) => setFilter('status', value)}>
        <SelectTrigger aria-label={t('people.statusFilter')} className="list-toolbar-chip">
          <span className="text-muted-foreground">{t('people.statusFilter')}:</span>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{t('people.all')}</SelectItem>
          {STATUSES.map((status) => (
            <SelectItem key={status} value={status}>
              {t(`people.statusOption.${status}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {/* Whenever a filter narrows the list, not only when nothing is left (the standard's Clear). */}
      {filtered ? (
        <Button variant="ghost" size="sm" onClick={onClear}>
          {t('people.noResults.clear')}
        </Button>
      ) : null}
      <span className="list-toolbar-spacer" />
      <Select
        value={filters.sort}
        onValueChange={(value) => setFilter('sort', value === 'name' ? null : value)}
      >
        <SelectTrigger aria-label={t('people.sortLabel')} className="list-toolbar-chip">
          <span className="text-muted-foreground">{t('people.sortLabel')}:</span>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="name">{t('people.sortOption.name')}</SelectItem>
          <SelectItem value="updated">{t('people.sortOption.updated')}</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
